import type { AxiosRequestConfig, AxiosResponse } from 'axios';
import omit from 'lodash/omit';
import type { RefObject } from 'react';
import ExecutionContext from '../../ExecutionContext';
import type { Money } from '../../Money.csharp';
import { axiosWrapper } from '../FetchData/axiosWrapper';
import { getContentLanguage } from '~/Features/Configuration/Utils/languageUtils';
import type { Falsy, StrictOmit } from '~/Partials/Utils/typeUtils';

export const omitProperty = <TObj extends object, TArgs extends keyof TObj>(
    object: TObj,
    ...args: TArgs[]
): StrictOmit<TObj, TArgs> => omit(object, args);

/**
 * compare values
 * TODO: this is temporary solution.
 * make it better and faster or use some small lib
 */
export function checkEquals<T>(a: T, b: T) {
    try {
        /** FIXME:
         * JSON.stringify "ignores" functions so there
         * is no sense to compare functions.. will always be true
         */
        return JSON.stringify(a) === JSON.stringify(b);
    } catch (e) {
        return false;
    }
}

/* TODO - needs some work with it */

// Replace with new makePostRequest
export async function makePostRequestOld<ResponseType = unknown, ParamsType = unknown>(
    url: string,
    params: ParamsType,
    config: AxiosRequestConfig = {}
) {
    // No try-catch on purposes.
    const response = await axiosWrapper.post(url, params, config);

    return response as AxiosResponse<ResponseType>;
}

export function objectAssignToEmptyFields<TObject extends object, TFields extends Partial<TObject>>(
    source: TObject,
    fields: TFields
) {
    /* TODO: test & doc & make code more readable maybe? */
    return Object.entries(source).reduce((acc, [key, oldVal]) => {
        const newVal = fields[key as keyof TObject];

        // Only non-boolean "falsy" value will be replaced.
        const isOldValueEmpty = () => !oldVal && typeof oldVal !== 'boolean';

        // And only if new value is "truthy" or boolean
        const isNewValueNonEmpty = () => !!newVal || typeof newVal === 'boolean';

        return {
            ...acc,
            [key]: isOldValueEmpty() && isNewValueNonEmpty() ? newVal : oldVal,
        };
    }, {} as TObject);
}

export function cloneArrayAndUpdateSingleItem<T>(source: T[], item: T, updateFunction: (oldItem: T) => T) {
    const index = source.indexOf(item);
    const updatedItem = updateFunction(item);

    return [...source.slice(0, index), updatedItem, ...source.slice(index + 1)];
}

export const moneyToString = (money: Money) => {
    const amountFormatted = formatAmount(money.amount);
    const currencyFormatted = formatCurrency(money.currencyCode);

    return money.currencyCode === 'NOK'
        ? `${currencyFormatted} ${amountFormatted}`
        : `${amountFormatted} ${currencyFormatted}`;
};

const formatAmount = (amount: number) => {
    const parts = amount.toString().split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

    return parts.join('.');
};

const formatCurrency = (currency: string) => (currency === 'NOK' ? 'kr' : currency);

export const formatNumber = (value: number) => value.toLocaleString(getContentLanguage());

export const redirect = (url: string) => {
    if (ExecutionContext.IsServerSide()) {
        return;
    }

    window.location.href = url.indexOf('/') === 0 ? `${window.location.origin}${url}` : url;
};

export const reload = () => {
    if (ExecutionContext.IsServerSide()) {
        return;
    }

    window.location.reload();
};

export const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
export const getElementById = (id: string) => {
    if (!ExecutionContext.IsServerSide()) {
        return document.getElementById(id);
    }
};
export const scrollToElement = (
    element: HTMLElement,
    behavior: ScrollBehavior = 'smooth',
    block?: ScrollLogicalPosition,
    inline?: ScrollLogicalPosition
) => {
    if (!ExecutionContext.IsServerSide()) {
        element.scrollIntoView({
            behavior,
            block,
            inline,
        });
    }
};

export const smoothScrollToSectionById = (id: string) => {
    const section = getElementById(id);

    if (section) {
        scrollToElement(section);
    }
};

export const smoothScrollToSectionByRef = (ref: RefObject<HTMLElement>) => {
    if (ref.current) {
        scrollToElement(ref.current);
    }
};

export const scrollToTop = () => scrollTo(0, 0);

export const isTruthy = <T>(item: T | Falsy): item is T => !!item;

export const getCurrentUrl = () => (ExecutionContext.IsServerSide() ? undefined : window.location.href);

export const toUrl = (url: string) => {
    ExecutionContext.ThrowIfServerSide();

    return new URL(url, `${location.protocol}//${location.hostname}${location.port ? `:${location.port}` : ''}`);
};

function legacyCopyTextToClipboard(text: string) {
    const textArea = document.createElement('textarea');
    textArea.value = text;

    // Avoid scrolling to bottom
    textArea.style.top = '0';
    textArea.style.left = '0';
    textArea.style.position = 'fixed';

    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
}

export async function copyTextToClipboard(text: string) {
    // eslint-disable-next-line compat/compat -- we first check whether the browser supports the clipboard API
    navigator.clipboard ? await navigator.clipboard.writeText(text) : legacyCopyTextToClipboard(text);
}

export function combineValidAttributeValues(...values: (string | undefined)[]) {
    const validValues = values.filter(isTruthy);

    return validValues.length === 0 ? undefined : validValues.join(' ');
}

export const repeat = <TItem>(item: TItem, count: number) => Array<TItem>(count).fill(item);

export const repeatWithId = <TItem>(item: TItem, count: number) =>
    repeat(item, count).map((element, index) => ({ id: index.toString(), ...element }));

export const createArray = <TItem>(factory: (index: number) => TItem, count: number) =>
    Array.from({ length: count }).map((_, index) => factory(index));

export const replaceNotFriendlyCharacters = (str: string) => str.replace(/[ -/:-@[-`{-}]+/g, '-');

export const capitalize = <T extends string>(str: T) =>
    (str ? str[0].toUpperCase() + str.slice(1) : str) as Capitalize<T>;

export const getLastElementOfArray = <TItem>(array?: TItem[]) =>
    !array || array.length === 0 ? undefined : array[array.length - 1];

export const stripFromHTMLNode = (rawHtml: string, node: 'p') =>
    rawHtml.replace(new RegExp(`<${node}>|</${node}>`, 'g'), '');

export const firstKey = <K, V>(map: Map<K, V>) => {
    const result = map.keys().next();

    return result.done ? undefined : result.value;
};

export const hasAny = <TItem>(items: TItem[], predicate: (item: TItem) => boolean) =>
    items.find(predicate) !== undefined;

export const areEqual = (first: string, second: string, ignoreCase = false) =>
    first.localeCompare(second, undefined, { sensitivity: ignoreCase ? 'accent' : 'variant' }) === 0;

export const isSafari = () => {
    if (ExecutionContext.IsServerSide()) {
        return false;
    }

    // eslint-disable-next-line compat/compat -- this is client-side code
    const userAgent = navigator.userAgent.toLowerCase();

    return userAgent.includes('safari') && !userAgent.includes('chrome') && !userAgent.includes('crios');
};

export const findClosestParent = <TElement extends Element>(
    element: Element,
    predicate: (element: Element) => element is TElement
): TElement | null => {
    let potentialResult: Element | null = element;

    while (potentialResult) {
        if (predicate(potentialResult)) {
            return potentialResult;
        }

        potentialResult = potentialResult.parentElement;
    }

    return null;
};

export const isInteractiveElement = (element: HTMLElement) => {
    const formTags = ['button', 'input', 'select', 'textarea'];
    const linkTags = ['a', 'area'];

    const normalizedTagName = element.tagName.toLowerCase();

    return (
        (formTags.includes(normalizedTagName) && !element.hasAttribute('disabled')) ||
        (linkTags.includes(normalizedTagName) && element.hasAttribute('href'))
    );
};

export const hashValue = async (value: string) => {
    ExecutionContext.ThrowIfServerSide();

    const encoder = new TextEncoder();
    const data = encoder.encode(value);
    // eslint-disable-next-line compat/compat -- this is client-side code
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));

    return hashArray.map(num => num.toString(16).padStart(2, '0')).join('');
};

export const isErrorWithName = (error: unknown, errorName: string) =>
    typeof error === 'object' && error !== null && 'name' in error && error.name === errorName;

export const urlJoin = (...parts: string[]) => parts.join('/').replace('//', '/');
